import { motion } from "framer-motion";
import {
  Briefcase, GitBranch, FileText, Clock, Database, Users, GraduationCap, ArrowUpRight,
} from "lucide-react";
import { fadeUp, staggerContainer, viewportOnce } from "@/lib/animations";

const services = [
  {
    icon: Briefcase,
    title: "PMO Setup & Optimization",
    desc: "Design and stand-up Project Management Offices that fit your operating model — from governance and KPIs to tooling and cadence.",
    span: "lg:col-span-7",
  },
  {
    icon: GitBranch,
    title: "End-to-End Project Lifecycle",
    desc: "Initiation to close-out, with risk, scope and stakeholder discipline.",
    span: "lg:col-span-5",
  },
  {
    icon: FileText,
    title: "RFQ-to-Order Consulting",
    desc: "Streamline quoting workflows, standardize templates, accelerate hand-offs.",
    span: "lg:col-span-5",
  },
  {
    icon: Clock,
    title: "On-Time Delivery Programs",
    desc: "Diagnose root causes of delays and engineer OTD improvement programs that move the needle — proven from 35% to 95%.",
    span: "lg:col-span-7",
  },
  {
    icon: Database,
    title: "ERP Implementation Support",
    desc: "Microsoft Dynamics AX · SAP · Oracle. Functional alignment, change management, and PM-side rollouts.",
    span: "lg:col-span-6",
  },
  {
    icon: Users,
    title: "Cross-functional Stakeholder Management",
    desc: "Bridge engineering, sales, operations, and finance with structured cadence and communication frameworks.",
    span: "lg:col-span-6",
  },
  {
    icon: GraduationCap,
    title: "Process Standardization & Training",
    desc: "Codify what works. Train teams. Build playbooks that survive personnel change.",
    span: "lg:col-span-12",
  },
];

export const Services = ({ onBookClick }) => {
  return (
    <section id="services" className="relative py-24 md:py-32 bg-[#0A192F]" data-testid="services-section">
      <div className="absolute inset-0 bg-grid-gold opacity-20 pointer-events-none" />
      <div className="relative px-6 md:px-12 lg:px-24">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="max-w-3xl mb-16"
        >
          <motion.div variants={fadeUp} className="flex items-center gap-4 mb-6">
            <div className="gold-rule" />
            <span className="text-xs uppercase tracking-[0.35em] text-[#D4AF37]">Services</span>
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-serif text-4xl md:text-5xl text-[#F8F9FA] leading-tight text-balance" data-testid="services-heading">
            Seven disciplines. <em className="text-[#D4AF37] font-medium">One outcome:</em> reliable delivery.
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-6 text-[#A8B2D1] text-lg leading-relaxed">
            Engagements are scoped to your organization's maturity — from greenfield PMOs to recovery of in-flight
            programs and ERP rollouts.
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="grid lg:grid-cols-12 gap-5"
        >
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              variants={fadeUp}
              whileHover={{ y: -4 }}
              className={`group relative bg-[#051024] border border-[#233554] hover:border-[#D4AF37]/60 transition-all duration-300 p-8 md:p-10 ${s.span} overflow-hidden`}
              data-testid={`service-card-${i}`}
            >
              <div className="absolute -top-px -right-px w-14 h-14 border-t border-r border-[#D4AF37]/0 group-hover:border-[#D4AF37] transition-colors duration-500" aria-hidden />
              <s.icon size={32} strokeWidth={1.25} className="text-[#D4AF37] mb-6" />
              <h3 className="font-serif text-xl md:text-2xl text-[#F8F9FA] mb-3">{s.title}</h3>
              <p className="text-[#A8B2D1] leading-relaxed">{s.desc}</p>
              <div className="mt-6 flex items-center text-xs uppercase tracking-[0.25em] text-[#A8B2D1] group-hover:text-[#D4AF37] transition-colors">
                <span>Discuss this</span>
                <ArrowUpRight size={14} className="ml-2" />
              </div>
              <button
                onClick={onBookClick}
                className="absolute inset-0 w-full h-full"
                aria-label={`Discuss ${s.title}`}
                data-testid={`service-cta-${i}`}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
